import React, { Fragment, PureComponent } from 'react';
import get from 'lodash/get';
import { message, Popover } from 'antd';
import { api } from '$app/api';
import { BOOKING_STATUS } from '$app/apps/bookings/containers/BookingPage/constants';
import Table from '$app/apps/bookings/components/Table';
import ActionButton from '$app/apps/bookings/components/ActionButton';
import Tag from '$app/apps/bookings/components/Tag';
import { colors, TableCard, TitleDiv } from './styled';
import {
  buildAssignHeroesTableData,
  buildTripsTableData,
  getBookingLatLng,
  normalizeHeroes,
} from './selectors';
import AssignHeroModal from './components/AssignHeroModal';
import { getPathfinderJourneys } from './queries';
import { DISTANCE_BASED_PRICING_TIERS } from './constants';
import BookingPageContext from '../context';

class HeroTrips extends PureComponent {
  static contextType = BookingPageContext;

  constructor(props) {
    super(props);

    this.state = {
      assignHeroModalVisible: false,
      assignSameHero: true,
      selectedTripId: 0,
      tripsGalleryModalVisible: false,
      heroes: [],
      isLoading: false,
      driverJourneys: [],
      garageJourney: [],
      journeysLoading: true,
    };

    this.getTableColumns = getTableColumns.bind(this);
    this.showAssignHeroModal = this.showAssignHeroModal.bind(this);
  }

  async componentDidMount() {
    await Promise.all([this.loadHeroes(), this.loadJourneys()]);
  }

  async componentDidUpdate(prevProps) {
    if (
      prevProps.booking.bookingId !== this.props.booking.bookingId ||
      prevProps.booking.garageId !== this.props.booking.garageId
    ) {
      await Promise.all([this.loadHeroes(), this.loadJourneys()]);
    }
  }

  loadHeroes = async () => {
    const { latitude, longitude } = getBookingLatLng(this.props.booking);

    if (!latitude || !longitude) return;

    try {
      const { data } = await api.get(`/drivers?lat=${latitude}&long=${longitude}`);
      this.setState({ heroes: normalizeHeroes(data) });
    } catch (error) {
      message.error(error.message);
    }
  };

  loadJourneys = async () => {
    const { booking } = this.props;

    this.setState({ journeysLoading: true });

    try {
      const [driverJourneys, garageJourney] = await Promise.all([
        getPathfinderJourneys({
          fromType: 'driver',
          toId: booking.bookingId.toString(),
          journeyType: 'collection-pickup',
        }),
        getPathfinderJourneys({
          fromId: booking.bookingId.toString(),
          toId: get(booking, 'garageId', ''),
          journeyType: 'collection-dropoff',
        }),
      ]);

      this.setState({
        driverJourneys: driverJourneys || [],
        garageJourney: garageJourney || [],
        journeysLoading: false,
      });
    } catch (error) {
      message.error(error.message || 'Error occurred');
      this.setState({
        driverJourneys: [],
        garageJourney: [],
        journeysLoading: false,
      });
    }
  };

  showAssignHeroModal(selectedTripId) {
    this.setState({
      assignHeroModalVisible: true,
      selectedTripId,
    });
  }

  handleAssignHeroModalOk = async (heroId, driverRate) => {
    const { selectedTripId, assignSameHero } = this.state;
    const { bookingId, trips = [] } = this.props.booking;
    const selectedTrip = trips.find((trip) => trip.tripId === selectedTripId);

    if (!selectedTrip) return;

    const tripAssignments = [
      {
        heroId,
        driverRate,
        bookingId,
        tripId: selectedTripId,
        allocationStatus: selectedTrip.heroId === heroId ? selectedTrip.allocationStatus : 'pending',
      },
    ];

    if (assignSameHero) {
      const relatedTrip = trips.find(({ status, tripId }) => status === 'active' && tripId !== selectedTripId);
      if (relatedTrip) {
        tripAssignments.push({
          heroId,
          driverRate,
          bookingId,
          tripId: relatedTrip.tripId,
          allocationStatus: selectedTrip.heroId === heroId ? relatedTrip.allocationStatus : 'pending',
        });
      }
    }

    this.setState({ isLoading: true });

    try {
      await Promise.all(
        tripAssignments.map(({ bookingId, tripId, heroId, driverRate, allocationStatus }) =>
          api.patch(`/bookings/${bookingId}/trips/${tripId}`, {
            pickerId: heroId,
            driverRate,
            allocationStatus: allocationStatus || 'pending',
          })
        )
      );

      this.context.updateTripAssignment(tripAssignments);
      this.setState({
        assignHeroModalVisible: false,
        isLoading: false,
        selectedTripId: 0,
      });
    } catch (error) {
      const errorCode = get(error, 'response.data.code');
      const errorMessage =
        errorCode === 'SCHEMA_VALIDATION_FAILED' ? 'Driver rate cannot be 0' : error.message;

      message.error(errorMessage);
      this.setState({ isLoading: false });
    }
  };

  handleAssignHeroModalCancel = () =>
    this.setState({ assignHeroModalVisible: false, isLoading: false });

  handleSameHeroModalChange = (e) => this.setState({ assignSameHero: e.target.checked });

  showTripsGalleryModal = () => this.setState({ tripsGalleryModalVisible: true });

  findDbpTier = (miles) => {
    const tier = DISTANCE_BASED_PRICING_TIERS.find(({ lower, upper }) => lower <= miles && miles <= upper);
    if (!tier) return null;
    return tier.name;
  };

  groupHeroesAndPaths = (heroes, journeys) =>
    heroes.map((hero) => {
      const path = journeys.find((journey) => journey.fromId === hero.driverUuid);
      const updatedHero = { ...hero, distance: (path && path.distance) || hero.distance };
      if (path) updatedHero.actualDistance = true;
      return updatedHero;
    });

  render() {
    const { booking } = this.props;
    const {
      heroes,
      isLoading,
      assignHeroModalVisible,
      selectedTripId,
      driverJourneys,
      garageJourney,
      journeysLoading,
    } = this.state;
    const tripsTableData = buildTripsTableData(booking, heroes);
    const assignHeroTableData = buildAssignHeroesTableData(heroes);
    const tableColumns = this.getTableColumns(booking);
    if (journeysLoading) return 'Loading trips..';

    let heroesWithJourneys = this.groupHeroesAndPaths(assignHeroTableData, driverJourneys);
    heroesWithJourneys = heroesWithJourneys.map((hero) => {
      hero.customerToGarageDistance = get(garageJourney, '[0].distance', 0);

      if (hero.distanceBasedPricing) {
        const tierKey = this.findDbpTier(hero.customerToGarageDistance);
        hero.rate = get(hero, tierKey, null);
      }
      return hero;
    });

    return (
      <Fragment>
        <TableCard className="functional_heroTrips">
          <TitleDiv>Hero Trips</TitleDiv>
          <Table dataSource={tripsTableData} columns={tableColumns} pagination={false} />
        </TableCard>
        <AssignHeroModal
          isLoading={isLoading}
          isVisible={assignHeroModalVisible}
          tripId={selectedTripId}
          tableData={heroesWithJourneys}
          handleAssignHeroModalOk={this.handleAssignHeroModalOk}
          handleAssignHeroModalCancel={this.handleAssignHeroModalCancel}
          handleSameHeroModalChange={this.handleSameHeroModalChange}
        />
      </Fragment>
    );
  }
}

const isAssignHeroBtnDisabled = (
  { collectionDeliveryOption, masterStatusId, transportStatus },
  tripStatus,
  tripLeg
) => {
  if ([BOOKING_STATUS.CLOSED, BOOKING_STATUS.CANCELLED].includes(masterStatusId)) return true;
  if (tripLeg === 'customer-garage') {
    if (
      tripStatus !== 'active' ||
      collectionDeliveryOption === 'GarageC&D' ||
      masterStatusId >= BOOKING_STATUS.AT_GARAGE
    ) {
      return true;
    }
  }

  if (tripLeg === 'garage-customer') {
    if (
      tripStatus !== 'active' ||
      collectionDeliveryOption === 'GarageC&D' ||
      transportStatus === 'DELIVERED'
    ) {
      return true;
    }
  }
};

const parseFaledReason = (reason) => {
  const leg1 = reason.split('-leg_divider-')[0];
  const leg2 = reason.split('-leg_divider-')[1];

  return (
    <div>
      <p>Leg 1: {leg1}</p>
      <p>Leg 2: {leg2}</p>
    </div>
  );
};

function getTableColumns(booking) {
  return [
    {
      title: 'Trip leg',
      dataIndex: 'tripLeg',
      key: 'tripLeg',
    },
    {
      title: 'Hero ID',
      dataIndex: 'heroId',
      key: 'heroId',
    },
    {
      title: 'Last leg',
      dataIndex: 'lastLeg',
      key: 'lastLeg',
      render: (leg) => (
        <>
          {leg[0]}{' '}
          {leg[1] && (
            <>
              <br /> {leg[1]}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        if (status === 'failed' && record.failedReason) {
          const failedReasons = parseFaledReason(record.failedReason);

          return (
            <Popover content={failedReasons} title="Failed reasons">
              <Tag color={colors[status]} key={status}>
                {status}
              </Tag>
            </Popover>
          );
        }

        return (
          <Tag color={colors[status]} key={status}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Allocation status',
      dataIndex: 'allocationStatus',
      key: 'allocationStatus',
      render: (allocationStatus) => {
        if (allocationStatus === 'accepted') {
          return (
            <Tag color={colors.accepted} key={allocationStatus}>
              {allocationStatus}
            </Tag>
          );
        }

        if (allocationStatus === 'pending') {
          return (
            <Tag color={colors.pending} key={allocationStatus}>
              {allocationStatus}
            </Tag>
          );
        }

        return (
          <Tag color={colors.unallocated} key={allocationStatus}>
            {allocationStatus}
          </Tag>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (val, { key, status, tripLeg }) => (
        <ActionButton
          disabled={isAssignHeroBtnDisabled(booking, status, tripLeg)}
          onClick={() => this.showAssignHeroModal(key)}
          src="/assets/images/cross-over.svg"
        >
          Hero
        </ActionButton>
      ),
    },
  ];
}

export default HeroTrips;
